import { EProjectPublishMethod, ICommonSettings } from "@kuika/kuika-cl-model";
import { PureComponent, ReactNode } from "react";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigSettingSelect } from "../config-setting-select";
import { ConfigGoogleAuthSettings } from "./app-settings-config-google-login";
import { ConfigMSALSettings } from "./app-settings-config-msal";
import { ConfigOTPSettings } from "./app-settings-config-otp";

export interface IConfigOtherLoginInputsState {
  showGoogleModal: boolean;
  showAppleModal: boolean;
  showMsalModal: boolean;
  showOtpModal: boolean;
  editingGoogleAuthId: string | null;
  editingMsalSettingId: string | null;
  editingOtpSettingId: string | null;
}

export interface IConfigOtherLoginInputsProps {
  publishMethod?: EProjectPublishMethod | "3" | "4" | "5" | "6";
  parentConfigGoogleAuthValue?: string | null;
  parentConfigMsalAuthValue?: string | null;
  parentConfigOtpAuthValue?: string | null;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleUpdateCustomFormInput?: (formItemName: string[], value: string | null) => void;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigOtherLoginInputs extends PureComponent<IConfigOtherLoginInputsProps, IConfigOtherLoginInputsState> {
  googleAuthFormInputName = ["apiConfig", "googleAuthId"];

  msalAuthFormInputName = ["apiConfig", "msalAuthId"];

  otpSettingsFormInputName = ["apiConfig", "otpSettingsId"];

  constructor(props: IConfigOtherLoginInputsProps) {
    super(props);
    this.state = {
      showGoogleModal: false,
      showAppleModal: false,
      showMsalModal: false,
      showOtpModal: false,
      editingGoogleAuthId: null,
      editingMsalSettingId: null,
      editingOtpSettingId: null
    };
  }

  openMsalModal = () => {
    this.setState({ editingMsalSettingId: "", showMsalModal: true });
  };

  openGoogleModal = () => {
    this.setState({ editingGoogleAuthId: "", showGoogleModal: true });
  };

  closeGoogleModal = () => {
    this.setState({ showGoogleModal: false });
  };

  closeMsalModal = () => {
    this.setState({ showMsalModal: false });
  };

  closeAppleModal = () => {
    this.setState({ showAppleModal: false });
  };

  openOtpModal = () => {
    this.setState({ editingOtpSettingId: "", showOtpModal: true });
  };

  closeOtpModal = () => {
    this.setState({ showOtpModal: false });
  };

  setEditingGoogleAuth = (id: string) => {
    this.setState({ editingGoogleAuthId: id, showGoogleModal: true });
  };

  setEditingMsal = (id: string) => {
    this.setState({ editingMsalSettingId: id, showMsalModal: true });
  };

  setEditingOtp = (id: string) => {
    this.setState({ editingOtpSettingId: id, showOtpModal: true });
  };

  restore = (formInputName: string[], parentValue: string | null | undefined): void => {
    this.props.handleUpdateCustomFormInput?.(formInputName, parentValue ?? null);
  };

  render() {
    const {
      parentConfigGoogleAuthValue,
      parentConfigMsalAuthValue,
      parentConfigOtpAuthValue,
      commonSettings,
      getSectionDescription,
      handleUpdateCustomFormInput,
      handleCreateCommonConfig,
      handleUpdateCommonConfig,
      handleDeleteCommonConfig
    } = this.props;

    return (
      <>
        <ConfigGoogleAuthSettings
          formInputName={this.googleAuthFormInputName}
          closeModal={this.closeGoogleModal}
          showModal={this.state.showGoogleModal}
          editingGoogleAuthId={this.state.editingGoogleAuthId}
          commonSettings={commonSettings}
          getSectionDescription={getSectionDescription}
          handleCreateCommonConfig={handleCreateCommonConfig}
          handleUpdateCommonConfig={handleUpdateCommonConfig}
          handleDeleteCommonConfig={handleDeleteCommonConfig}
        />

        <ConfigMSALSettings
          formInputName={this.msalAuthFormInputName}
          closeModal={this.closeMsalModal}
          showModal={this.state.showMsalModal}
          editingMsalId={this.state.editingMsalSettingId}
          commonSettings={commonSettings}
          getSectionDescription={getSectionDescription}
          handleCreateCommonConfig={handleCreateCommonConfig}
          handleUpdateCommonConfig={handleUpdateCommonConfig}
          handleDeleteCommonConfig={handleDeleteCommonConfig}
        />

        <ConfigOTPSettings
          formInputName={this.otpSettingsFormInputName}
          closeModal={this.closeOtpModal}
          showModal={this.state.showOtpModal}
          editingOtpId={this.state.editingOtpSettingId}
          commonSettings={commonSettings}
          getSectionDescription={getSectionDescription}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />

        <ConfigSettingSelect
          label="Google"
          formInputName={this.googleAuthFormInputName}
          options={commonSettings.googleAuth ?? []}
          allowClear
          tooltipContentKey="appsettings_authentication_authorization_google"
          onAddNew={this.openGoogleModal}
          onEdit={this.setEditingGoogleAuth}
          parentConfigValue={parentConfigGoogleAuthValue}
          onRestore={() => this.restore(this.googleAuthFormInputName, parentConfigGoogleAuthValue)}
          dropdownTestId="appSettingsAuthGoogleDropdown"
          addNewTestId="appSettingsAuthGoogleAddNewButton"
          editTestId="appSettingsAuthGoogleEditButton"
        />

        <ConfigSettingSelect
          label="MSAL"
          formInputName={this.msalAuthFormInputName}
          options={commonSettings.msalSettings ?? []}
          allowClear
          tooltipContentKey="appsettings_msal_configuration"
          onAddNew={this.openMsalModal}
          onEdit={this.setEditingMsal}
          parentConfigValue={parentConfigMsalAuthValue}
          onRestore={() => this.restore(this.msalAuthFormInputName, parentConfigMsalAuthValue)}
          dropdownTestId="appSettingsAuthMsalDropdown"
          addNewTestId="appSettingsAuthMsalAddNewButton"
          editTestId="appSettingsAuthMsalEditButton"
        />

        <ConfigSettingSelect
          label="OTP"
          formInputName={this.otpSettingsFormInputName}
          options={commonSettings.otpSettings ?? []}
          allowClear
          tooltipContentKey="appsettings_otp_description"
          onAddNew={this.openOtpModal}
          onEdit={this.setEditingOtp}
          parentConfigValue={parentConfigOtpAuthValue}
          onRestore={() => this.restore(this.otpSettingsFormInputName, parentConfigOtpAuthValue)}
          dropdownTestId="appSettingsAuthOtpDropdown"
          addNewTestId="appSettingsAuthOtpAddNewButton"
          editTestId="appSettingsAuthOtpEditButton"
        />
      </>
    );
  }
}
